import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@bowfast/ui/components/empty";
import { Skeleton } from "@bowfast/ui/components/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenIcon, PlusIcon, ShieldCheckIcon, TrendingUpIcon } from "lucide-react";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { CategoryCard } from "~/components/skills/CategoryCard";
import { CategoryDialog } from "~/components/skills/CategoryDialog";
import { ImportTemplateDialog } from "~/components/skills/ImportTemplateDialog";
import type { SkillFormData } from "~/components/skills/SkillDialog";
import { SkillDialog } from "~/components/skills/SkillDialog";
import { SkillsSummary } from "~/components/skills/SkillsSummary";
import { TemplateCard } from "~/components/skills/TemplateCard";
import type {
  CreateSkillCategoryInput,
  CreateSkillInput,
  UpdateSkillCategoryInput,
  UpdateSkillInput,
} from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { useOrg } from "~/lib/org-context";
import type { ActiveView, Skill, SkillCategory, SkillTemplate } from "./skills.types";

// ── Page ──────────────────────────────────────────────────────────────────────

export function SkillsPage() {
  const auth = useAuth();
  const { orgId } = useOrg();
  const queryClient = useQueryClient();
  const token = auth.user?.access_token ?? "";
  const sdk = createSdk(token);

  const [view, setView] = useState<ActiveView>("framework");

  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; category?: SkillCategory }>({ open: false });
  const [skillDialog, setSkillDialog] = useState<{ open: boolean; skill?: Skill; categoryId?: string }>({
    open: false,
  });
  const [importDialog, setImportDialog] = useState<{ open: boolean; template?: SkillTemplate }>({ open: false });

  // ── Queries ────────────────────────────────────────────────────────────────

  const {
    data: categoriesData,
    isPending: isCategoriesPending,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ["skill-categories", orgId],
    queryFn: () => sdk.SkillCategoriesList({ orgId }),
    enabled: !!token && !!orgId,
    staleTime: 60 * 1000,
  });

  const {
    data: templatesData,
    isPending: isTemplatesPending,
    isError: isTemplatesError,
    refetch: refetchTemplates,
  } = useQuery({
    queryKey: ["skill-templates"],
    queryFn: () => sdk.SkillTemplatesList(),
    enabled: !!token && view === "templates",
    staleTime: 5 * 60 * 1000,
  });

  const categories = categoriesData?.skillCategories ?? [];
  const templates = templatesData?.skillTemplates ?? [];

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidateCategories = () => queryClient.invalidateQueries({ queryKey: ["skill-categories", orgId] });

  const createCategoryMutation = useMutation({
    mutationFn: (input: CreateSkillCategoryInput) => sdk.CreateSkillCategory({ orgId, input }),
    onSuccess: () => {
      invalidateCategories();
      setCategoryDialog({ open: false });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSkillCategoryInput }) =>
      sdk.UpdateSkillCategory({ id, input }),
    onSuccess: () => {
      invalidateCategories();
      setCategoryDialog({ open: false });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => sdk.DeleteSkillCategory({ id }),
    onSuccess: invalidateCategories,
  });

  const createSkillMutation = useMutation({
    mutationFn: ({ categoryId, input }: { categoryId: string; input: CreateSkillInput }) =>
      sdk.CreateSkill({ orgId, categoryId, input }),
    onSuccess: () => {
      invalidateCategories();
      setSkillDialog({ open: false });
    },
  });

  const updateSkillMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSkillInput }) => sdk.UpdateSkill({ id, input }),
    onSuccess: () => {
      invalidateCategories();
      setSkillDialog({ open: false });
    },
  });

  const deleteSkillMutation = useMutation({
    mutationFn: (id: string) => sdk.DeleteSkill({ id }),
    onSuccess: invalidateCategories,
  });

  const importTemplateMutation = useMutation({
    mutationFn: (templateKey: string) => sdk.ImportSkillTemplate({ orgId, templateKey }),
    onSuccess: () => {
      invalidateCategories();
      setImportDialog({ open: false });
      setView("framework");
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const activeCategoryName = skillDialog.categoryId
    ? (categories.find((c) => c.id === skillDialog.categoryId)?.name ?? "")
    : (categories.find((c) => c.skills.some((s) => s.id === skillDialog.skill?.id))?.name ?? "");

  function handleAddSkill(categoryId: string) {
    setSkillDialog({ open: true, categoryId });
  }
  function handleEditSkill(skill: Skill) {
    setSkillDialog({ open: true, skill });
  }
  function handleDeleteSkill(id: string) {
    deleteSkillMutation.mutate(id);
  }
  function handleEditCategory(c: SkillCategory) {
    setCategoryDialog({ open: true, category: c });
  }
  function handleDeleteCategory(id: string) {
    deleteCategoryMutation.mutate(id);
  }
  function handleImport(template: SkillTemplate) {
    setImportDialog({ open: true, template });
  }

  function handleSaveCategory(name: string, description: string | null) {
    if (categoryDialog.category) {
      updateCategoryMutation.mutate({
        id: categoryDialog.category.id,
        input: { name, ...(description ? { description } : {}) },
      });
    } else {
      createCategoryMutation.mutate({ name, ...(description ? { description } : {}) });
    }
  }

  function handleSaveSkill(data: SkillFormData) {
    const input = {
      name: data.name,
      ratingSystem: data.ratingSystem,
      ...(data.description ? { description: data.description } : {}),
      ...(data.level ? { level: data.level } : {}),
    };
    if (skillDialog.skill) {
      updateSkillMutation.mutate({ id: skillDialog.skill.id, input });
    } else if (skillDialog.categoryId) {
      createSkillMutation.mutate({ categoryId: skillDialog.categoryId, input });
    }
  }

  function handleImportConfirm() {
    if (importDialog.template) {
      importTemplateMutation.mutate(importDialog.template.key);
    }
  }

  const isEmpty = !isCategoriesPending && categories.length === 0;
  const isCategorySaving = createCategoryMutation.isPending || updateCategoryMutation.isPending;
  const isSkillSaving = createSkillMutation.isPending || updateSkillMutation.isPending;

  return (
    <div className="mx-auto w-full max-w-[72rem] space-y-6 p-4 md:p-6">
      {/* ── Page header ── */}
      <motion.div {...fadeUp} className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-[1.375rem] font-bold tracking-[-0.01em]">Skills framework</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Define your org's competency framework. Skippers and instructors assess crew against these skills during
            expeditions.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            variant="outline"
            className={
              view === "templates" ? "gap-2 border-abyss-500 bg-abyss-50 text-abyss-700 dark:bg-abyss-950" : "gap-2"
            }
            onClick={() => setView(view === "templates" ? "framework" : "templates")}
          >
            <BookOpenIcon className="size-3.5" />
            {view === "templates" ? "Back to framework" : "Import template"}
          </Button>
          {view === "framework" && (
            <Button size="sm" className="gap-2" onClick={() => setCategoryDialog({ open: true })}>
              <PlusIcon className="size-3.5" />
              New category
            </Button>
          )}
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {view === "framework" ? (
          <motion.div
            key="framework"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="space-y-5"
          >
            {isCategoriesError ? (
              <ListLoadError title="Couldn't load skill categories" onRetry={refetchCategories} />
            ) : isCategoriesPending ? (
              <div className="space-y-4">
                {["a", "b", "c"].map((k) => (
                  <Skeleton key={k} className="h-48 w-full rounded-xl" />
                ))}
              </div>
            ) : isEmpty ? (
              <motion.div {...fadeUp}>
                <Card>
                  <CardContent className="py-16">
                    <Empty>
                      <EmptyHeader>
                        <EmptyMedia variant="icon">
                          <ShieldCheckIcon />
                        </EmptyMedia>
                        <EmptyTitle>No skills defined.</EmptyTitle>
                        <EmptyDescription>Build your skill framework to start assessing crew.</EmptyDescription>
                      </EmptyHeader>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" className="gap-2" onClick={() => setCategoryDialog({ open: true })}>
                          <PlusIcon className="size-3.5" />
                          New skill category
                        </Button>
                        <Button size="sm" variant="outline" className="gap-2" onClick={() => setView("templates")}>
                          <BookOpenIcon className="size-3.5" />
                          Import a template
                        </Button>
                      </div>
                    </Empty>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <>
                <motion.div {...fadeUp}>
                  <SkillsSummary categories={categories} />
                </motion.div>

                <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-4">
                  {categories.map((category) => (
                    <motion.div key={category.id} variants={fadeUp}>
                      <CategoryCard
                        category={category}
                        onAddSkill={handleAddSkill}
                        onEditCategory={handleEditCategory}
                        onDeleteCategory={handleDeleteCategory}
                        onEditSkill={handleEditSkill}
                        onDeleteSkill={handleDeleteSkill}
                      />
                    </motion.div>
                  ))}

                  <motion.div variants={fadeUp}>
                    <button
                      type="button"
                      onClick={() => setCategoryDialog({ open: true })}
                      className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border px-5 py-4 text-sm text-muted-foreground transition-colors hover:border-abyss-300 hover:text-abyss-600 dark:hover:border-abyss-700 dark:hover:text-abyss-400"
                    >
                      <PlusIcon className="size-4" />
                      Add another category
                    </button>
                  </motion.div>
                </motion.div>
              </>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="templates"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="space-y-5"
          >
            <div className="flex items-center gap-3 rounded-xl border border-border bg-abyss-50/60 px-5 py-4 dark:bg-abyss-950/40">
              <TrendingUpIcon className="size-5 shrink-0 text-abyss-500" />
              <div>
                <p className="text-sm font-semibold">Platform templates</p>
                <p className="text-xs text-muted-foreground">
                  Import a pre-built framework (RYA, STCW, IYT, ASA). After import, your org owns the copy — rename,
                  reorder, or remove freely. Templates are not updated retroactively.
                </p>
              </div>
            </div>

            {isTemplatesError ? (
              <ListLoadError title="Couldn't load templates" onRetry={refetchTemplates} />
            ) : isTemplatesPending ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {["a", "b", "c", "d"].map((k) => (
                  <Skeleton key={k} className="h-48 w-full rounded-xl" />
                ))}
              </div>
            ) : (
              <motion.div
                initial="initial"
                animate="animate"
                variants={stagger}
                className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2"
              >
                {templates.map((template) => (
                  <motion.div key={template.id} variants={fadeUp}>
                    <TemplateCard template={template} onImport={() => handleImport(template)} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Dialogs ── */}
      <CategoryDialog
        open={categoryDialog.open}
        onOpenChange={(v) => setCategoryDialog({ open: v })}
        {...(categoryDialog.category ? { category: categoryDialog.category } : {})}
        onSave={handleSaveCategory}
        {...(isCategorySaving ? { isSaving: true } : {})}
      />

      <SkillDialog
        open={skillDialog.open}
        onOpenChange={(v) => setSkillDialog({ open: v })}
        {...(skillDialog.skill ? { skill: skillDialog.skill } : {})}
        categoryName={activeCategoryName}
        onSave={handleSaveSkill}
        {...(isSkillSaving ? { isSaving: true } : {})}
      />

      <ImportTemplateDialog
        open={importDialog.open}
        onOpenChange={(v) => setImportDialog({ open: v })}
        templateName={importDialog.template?.name ?? ""}
        onConfirm={handleImportConfirm}
        {...(importTemplateMutation.isPending ? { isImporting: true } : {})}
      />
    </div>
  );
}
